import { cn } from '@/lib/cn';

interface Option<T> {
  value: T;
  label: string;
  tone?: 'danger' | 'warning' | 'default';
}

interface Props<T extends string> {
  label: string;
  value: T;
  options: Option<T>[];
  onChange: (value: T) => void;
  /** Tighter segments for dense panels like task detail. */
  compact?: boolean;
  className?: string;
}

/**
 * One-tap choice between a few options.
 *
 * A select would be one tap to open, one to scan, one to pick. For a four-way
 * choice that is three interactions where one would do, and the current value
 * is invisible until you open it.
 */
export function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
  compact = false,
  className,
}: Props<T>) {
  return (
    <fieldset className={className}>
      <legend
        className={cn(
          'font-medium text-text-secondary',
          compact ? 'mb-1 text-label uppercase tracking-wide' : 'mb-2 text-small',
        )}
      >
        {label}
      </legend>
      <div
        role="radiogroup"
        aria-label={label}
        className={cn(
          'flex rounded-lg bg-surface-2 shadow-[inset_0_1px_0_oklch(1_0_0/0.04)]',
          compact ? 'gap-px p-px' : 'gap-1 p-1',
        )}
      >
        {options.map((option) => {
          const active = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange(option.value)}
              className={cn(
                'pressable flex-1 rounded-md text-center transition-[color,background-color,transform]',
                'duration-press ease-[cubic-bezier(0.32,0.72,0,1)]',
                compact
                  ? 'min-h-control min-w-0 truncate px-1 py-1 text-label leading-none'
                  : 'min-h-touch min-w-0 truncate px-2 text-small',
                active
                  ? 'bg-surface-1 font-medium text-text shadow-chip'
                  : 'text-text-secondary hover:text-text',
                active && option.tone === 'danger' && 'text-danger',
                active && option.tone === 'warning' && 'text-warning',
              )}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}
